'use client';

import type { ReactNode } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { cn } from '@kit/ui/utils';

import type { ProfileSummary } from './profile-types';

export type { ProfileSummary } from './profile-types';

/** Initials from a display name (avatar fallback). */
export function getProfileInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
  }
  return (name[0] ?? '').toUpperCase();
}

/**
 * The canonical rich profile row: avatar (initials fallback), display name,
 * and a secondary line. `trailing` slots a right-edge affordance (a check for
 * a selected combobox option, an event-scoped badge). `subtitle`, when
 * provided, REPLACES the default `@handle` secondary line — surfaces feed in
 * richer context (e.g. a dancer's dance styles) without the row needing to
 * know about it. Mirror of `OrganizationRow` so org and profile pickers render
 * identically.
 */
export function ProfileRow({
  profile,
  trailing,
  subtitle,
  className,
}: {
  profile: ProfileSummary;
  trailing?: ReactNode;
  /** Overrides the `@handle` secondary line when non-null. */
  subtitle?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex min-w-0 flex-1 items-center gap-3', className)}>
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarImage src={profile.avatarUrl ?? undefined} />
        <AvatarFallback className="text-xs">
          {getProfileInitials(profile.displayName)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="text-foreground truncate text-sm font-medium">
          {profile.displayName}
        </div>
        {subtitle != null ? (
          <div className="text-muted-foreground truncate text-xs">
            {subtitle}
          </div>
        ) : profile.handle ? (
          <div className="text-muted-foreground truncate text-xs">
            @{profile.handle}
          </div>
        ) : null}
      </div>
      {trailing}
    </div>
  );
}
